/**
 * The plan form's rules, apart from the form so they can be tested and so the
 * component file exports only components.
 */

/** The three monthly student allowances, in the order the plan page lists them. */
export const STUDENT_LIMITS = [
  { key: 'ai_cv_reviews_per_month', label: 'AI CV reviews' },
  { key: 'ai_roadmaps_per_month', label: 'AI roadmaps' },
  { key: 'applications_per_month', label: 'Applications' },
];

/** A blank limit is unlimited; anything else must be a whole number. */
export function optionalLimit(value) {
  const trimmed = value.trim();
  return trimmed === '' ? null : Number(trimmed);
}

/**
 * The code the server will make from the name, shown while typing so the
 * admin sees it without having to choose it. The server adds -2, -3… when
 * it is taken, and that is the one it keeps.
 */
export function planCodeFrom(name) {
  const slug = name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 36)
    .replace(/-+$/, '');
  if (!slug) return '';
  return /^[a-z]/.test(slug) ? slug : `plan-${slug}`.slice(0, 36).replace(/-+$/, '');
}

/** A whole number between `min` and `max`, or an error message. */
function wholeNumber(value, { min, max, label }) {
  const trimmed = String(value).trim();
  if (trimmed === '') return `Enter ${label}.`;
  const number = Number(trimmed);
  if (!Number.isFinite(number)) return 'Enter a number.';
  if (!Number.isInteger(number)) return 'Use a whole number.';
  if (number < min) return `Enter ${min} or more.`;
  if (number > max) return `Enter ${max.toLocaleString()} or less.`;
  return null;
}

/** Every rule mirrors `PlanWrite` in app/schemas/billing.py, so a form that
 *  passes here is one the server accepts. */
/**
 * `freeAllowed`: only the seeded free plans may cost nothing -- any other plan,
 * new or edited, needs a price above 0 (the API refuses 0 too).
 */
export function validatePlan(form, { freeAllowed = false } = {}) {
  const errors = {};

  const name = form.name.trim();
  if (name.length === 0) errors.name = 'Give the plan a name.';
  else if (name.length < 2) errors.name = 'Use at least 2 characters.';
  else if (name.length > 80) errors.name = 'Keep it under 80 characters.';
  else if (!/\p{L}/u.test(name)) errors.name = 'Use words, not only numbers or symbols.';

  if (form.description.trim().length > 2000) {
    errors.description = 'Keep it under 2,000 characters.';
  }

  const priceText = String(form.price).trim();
  const price = Number(priceText);
  if (priceText === '') errors.price = 'Enter a price.';
  else if (!Number.isFinite(price)) errors.price = 'Enter a number.';
  else if (price < 0) errors.price = 'A price cannot be negative.';
  else if (price === 0 && !freeAllowed) errors.price = 'Enter a price above 0.';
  else if (price > 100000) errors.price = 'Enter 100,000 or less.';
  else if (!/^\d+(\.\d{1,2})?$/.test(priceText)) errors.price = 'Use at most 2 decimal places.';

  const sortError = wholeNumber(form.sort_order, { min: 0, max: 1000, label: 'a position' });
  if (sortError) errors.sort_order = sortError;

  if (form.audience === 'STUDENT') {
    for (const { key } of STUDENT_LIMITS) {
      if (form[`${key}_unlimited`]) continue;
      const error = wholeNumber(form[key], { min: 0, max: 10000, label: 'a number' });
      if (error) errors[key] = `${error.replace(/\.$/, '')}, or tick Unlimited.`;
    }
    return errors;
  }

  const history = wholeNumber(form.analytics_history_days, {
    min: 1,
    max: 3650,
    label: 'the number of days',
  });
  if (history) errors.analytics_history_days = history;

  if (form.max_active_internships.trim() !== '') {
    const error = wholeNumber(form.max_active_internships, { min: 0, max: 10000, label: '' });
    if (error) errors.max_active_internships = error;
  }
  if (form.max_team_members.trim() !== '') {
    const error = wholeNumber(form.max_team_members, { min: 1, max: 10000, label: '' });
    if (error) errors.max_team_members = error;
  }
  return errors;
}
